import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { SwitchableUser, SwitchUserService } from '@app/auth/switch-user.service';

/**
 * TVG-378: the admin's list of every user on the platform, staff and
 * customers, with role and (for customers) event status. Choosing a row signs
 * the admin in as that user.
 */
@Component({
  selector: 'app-switch-user-modal',
  imports: [ModalComponent, TextInputComponent],
  templateUrl: './switch-user-modal.component.html',
  standalone: true,
})
export class SwitchUserModalComponent implements OnChanges {
  @Input() show = false;
  @Output() onClose = new EventEmitter<void>();

  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected users: SwitchableUser[] = [];
  protected loading = false;
  protected switchingToId: number | null = null;
  protected error: string | null = null;

  constructor(private switchUserService: SwitchUserService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['show']?.currentValue && this.users.length === 0) {
      this.load();
    }
  }

  protected get filteredUsers(): SwitchableUser[] {
    const term = this.searchControl.value.trim().toLowerCase();
    if (!term) {
      return this.users;
    }

    return this.users.filter((user) =>
      [user.name, user.email, user.role ?? '', ...user.events.map((event) => event.status)]
        .some((value) => value.toLowerCase().includes(term)),
    );
  }

  protected switchTo(user: SwitchableUser): void {
    if (this.switchingToId !== null) {
      return;
    }
    this.switchingToId = user.id;
    this.error = null;
    this.switchUserService.switchTo(user).subscribe({
      error: () => {
        this.switchingToId = null;
        this.error = `Could not switch to ${user.name}. Please try again.`;
      },
    });
  }

  protected roleLabel(role: string | null): string {
    return role ? role.charAt(0).toUpperCase() + role.slice(1) : 'No role';
  }

  private load(): void {
    this.loading = true;
    this.error = null;
    this.switchUserService.users().subscribe({
      next: (users) => {
        this.users = users;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = 'Could not load users. Please try again.';
      },
    });
  }
}
